import { useState } from "react";
import type { AuthUser } from "../types";

interface Props {
  user: AuthUser;
  onRefreshUser: () => void;
}

type Step = "idle" | "setup" | "disable";

/** Seção de 2FA (TOTP) opcional dentro de "Meus dados" - cada pessoa liga/desliga na própria conta. */
export function TwoFactorSection({ user, onRefreshUser }: Props) {
  const [step, setStep] = useState<Step>("idle");
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [confirmCode, setConfirmCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [disablePassword, setDisablePassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startSetup = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/auth/2fa/setup", { method: "POST" });
      const j = await r.json().catch(() => ({}) as { error?: string });
      if (!r.ok) {
        setError(j.error ?? "falha ao iniciar o setup do 2FA");
        return;
      }
      setQrDataUrl(j.qrDataUrl);
      setSecret(j.secret);
      setStep("setup");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/auth/2fa/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: confirmCode.trim() }),
      });
      const j = await r.json().catch(() => ({}) as { error?: string });
      if (!r.ok) {
        setError(j.error ?? "código inválido");
        return;
      }
      setBackupCodes(j.backupCodes);
      setConfirmCode("");
      onRefreshUser();
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/auth/2fa/disable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: disablePassword }),
      });
      const j = await r.json().catch(() => ({}) as { error?: string });
      if (!r.ok) {
        setError(j.error ?? "falha ao desativar");
        return;
      }
      setStep("idle");
      setDisablePassword("");
      onRefreshUser();
    } finally {
      setBusy(false);
    }
  };

  const regenerateBackupCodes = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch("/api/auth/2fa/backup-codes/regenerate", { method: "POST" });
      const j = await r.json().catch(() => ({}) as { error?: string });
      if (!r.ok) {
        setError(j.error ?? "falha ao gerar novos códigos");
        return;
      }
      setBackupCodes(j.backupCodes);
    } finally {
      setBusy(false);
    }
  };

  // backup codes recém-gerados (setup ou regeneração) - mostra até a pessoa fechar
  if (backupCodes) {
    return (
      <section className="about-section">
        <h2>Códigos de backup</h2>
        <p className="admin-user-meta" style={{ marginBottom: 10 }}>
          Guarde estes códigos em lugar seguro. Cada um só funciona <strong>uma vez</strong> e serve
          pra entrar se você perder o acesso ao app autenticador. Eles não serão mostrados de novo.
        </p>
        <div className="backup-codes-grid">
          {backupCodes.map((c) => (
            <code key={c}>{c}</code>
          ))}
        </div>
        <button
          className="btn-approve"
          type="button"
          style={{ marginTop: 12 }}
          onClick={() => {
            setBackupCodes(null);
            setStep("idle");
          }}
        >
          Já guardei - fechar
        </button>
      </section>
    );
  }

  return (
    <section className="about-section">
      <h2>Autenticação de dois fatores (2FA)</h2>
      <p className="admin-user-meta" style={{ marginBottom: 10 }}>
        Exige um código do seu celular (além da senha) pra entrar - recomendado se você acessa de
        redes compartilhadas ou tem conteúdo sensível nas conversas.
      </p>

      {user.twoFactorEnabled ? (
        step === "disable" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 360 }}>
            <label className="field">
              <span>Confirme sua senha pra desativar</span>
              <input type="password" value={disablePassword} onChange={(e) => setDisablePassword(e.target.value)} />
            </label>
            {error && <div className="auth-error">{error}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn-reject" disabled={busy} onClick={() => void disable()} type="button">
                {busy ? "Desativando…" : "Confirmar desativação"}
              </button>
              <button className="btn-approve" disabled={busy} onClick={() => setStep("idle")} type="button">
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className="badge ok">2FA ativo</span>
            <button className="btn-reject" type="button" onClick={() => setStep("disable")}>
              Desativar 2FA
            </button>
            <button className="btn-approve" type="button" disabled={busy} onClick={() => void regenerateBackupCodes()}>
              Gerar novos códigos de backup
            </button>
          </div>
        )
      ) : step === "setup" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 360 }}>
          <p className="admin-user-meta">
            Escaneie o QR com seu app autenticador (Google Authenticator, Authy, 1Password…) e
            informe o código gerado.
          </p>
          {qrDataUrl && <img src={qrDataUrl} alt="QR code do 2FA" width={180} height={180} />}
          {secret && (
            <p className="admin-user-meta">
              Não consegue escanear? Digite manualmente: <code>{secret}</code>
            </p>
          )}
          <label className="field">
            <span>Código de 6 dígitos</span>
            <input
              type="text"
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              placeholder="000000"
              autoComplete="one-time-code"
            />
          </label>
          {error && <div className="auth-error">{error}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-approve" disabled={busy} onClick={() => void confirmSetup()} type="button">
              {busy ? "Confirmando…" : "Confirmar e ativar"}
            </button>
            <button className="btn-reject" disabled={busy} onClick={() => setStep("idle")} type="button">
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button className="btn-approve" type="button" disabled={busy} onClick={() => void startSetup()}>
          Ativar 2FA
        </button>
      )}
    </section>
  );
}
